import type { Operation, JsonSchemaFragment } from "@keystone/core";

/**
 * Convert OpenAPI operation parameters + requestBody → a JSON-Schema object suitable
 * for the MCP `inputSchema` field. We deliberately stay on draft-07-ish syntax (the
 * MCP-friendly intersection — OpenAI clients reject `oneOf`/`allOf`, so we collapse
 * those, and we drop `nullable` in favor of explicit `null` type when needed).
 *
 * Output schema is the 2xx response body schema (preferred 200; falls back to first 2xx).
 */

export function buildInputSchema(op: Operation): JsonSchemaFragment {
  const properties: Record<string, unknown> = {};
  const required: string[] = [];

  for (const p of op.parameters) {
    // We surface query / path / header params as flat properties for ergonomics.
    // Body is merged below.
    if (p.location === "body") continue;
    properties[p.name] = sanitize(p.schema);
    if (p.required) required.push(p.name);
  }

  if (op.requestBody?.schema) {
    const body = sanitize(op.requestBody.schema) as Record<string, unknown>;
    if (body && typeof body === "object" && body.type === "object" && body.properties) {
      for (const [k, v] of Object.entries(body.properties as Record<string, unknown>)) {
        properties[k] = v;
      }
      if (Array.isArray(body.required) && op.requestBody.required) {
        for (const k of body.required as string[]) required.push(k);
      }
    } else if (body) {
      properties.body = body;
      if (op.requestBody.required) required.push("body");
    }
  }

  return {
    type: "object",
    properties,
    required: Array.from(new Set(required)),
    additionalProperties: false
  };
}

export function buildOutputSchema(op: Operation): JsonSchemaFragment {
  // Prefer 200, then 201, then any 2xx, else `default`.
  const ordered = [...op.responses].sort((a, b) => statusRank(a.status) - statusRank(b.status));
  const winner = ordered[0];
  if (!winner?.schema) return { type: "object" };
  return sanitize(winner.schema);
}

function statusRank(status: string): number {
  if (status === "200") return 0;
  if (status === "201") return 1;
  if (status === "204") return 2;
  if (/^2\d\d$/.test(status)) return 3;
  if (status === "default") return 10;
  return 20;
}

/**
 * Strip OpenAPI-isms that some MCP clients reject:
 *  - `nullable: true` → add `null` to `type` (3.0 → JSON Schema)
 *  - `oneOf` / `allOf` are collapsed to `anyOf` (OpenAI clients only support anyOf)
 *  - `discriminator` is dropped (not in JSON Schema draft-07)
 *  - `example` / `examples` are preserved (model finds them helpful)
 *  - `$ref` strings are passed through (caller is expected to have resolved local refs)
 */
function sanitize(input: unknown): JsonSchemaFragment {
  if (input === null || typeof input !== "object") return input as JsonSchemaFragment;
  if (Array.isArray(input)) return input.map(sanitize);
  const obj = { ...(input as Record<string, unknown>) };
  if (obj.nullable === true) {
    const t = obj.type;
    if (typeof t === "string") obj.type = [t, "null"];
    delete obj.nullable;
  }
  if (Array.isArray(obj.oneOf)) {
    obj.anyOf = [...(Array.isArray(obj.anyOf) ? (obj.anyOf as unknown[]) : []), ...(obj.oneOf as unknown[])];
    delete obj.oneOf;
  }
  if (Array.isArray(obj.allOf)) {
    obj.anyOf = [...(Array.isArray(obj.anyOf) ? (obj.anyOf as unknown[]) : []), ...(obj.allOf as unknown[])];
    delete obj.allOf;
  }
  delete obj.discriminator;
  for (const [k, v] of Object.entries(obj)) {
    obj[k] = sanitize(v);
  }
  return obj as JsonSchemaFragment;
}
